import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { serviciosService } from '@/services/serviciosService'
import { usePermissions } from '@/hooks/usePermissions'
import { assertPermiso } from '@/lib/permisos-guard'
import { conRefetchSoloPermitido } from '@/lib/query-permisos'
import type { Database } from '@/types/database'

type ServicioInsert = Database['public']['Tables']['servicios']['Insert']
type ServicioUpdate = Database['public']['Tables']['servicios']['Update']

// Query keys
export const serviciosKeys = {
  all: ['servicios'] as const,
  lists: () => [...serviciosKeys.all, 'list'] as const,
  list: () => [...serviciosKeys.lists()] as const,
  details: () => [...serviciosKeys.all, 'detail'] as const,
  detail: (id: string) => [...serviciosKeys.details(), id] as const,
  clientes: (id: string) => [...serviciosKeys.all, 'clientes', id] as const,
  cliente: (id: string) => [...serviciosKeys.all, 'cliente', id] as const,
}

// Hook para listar servicios
// Sin verHonorariosCliente el catálogo llega sin precio_estimado ni precio_base. Espera a los
// permisos resueltos: con los overrides del rol a medio cargar pediría sin precio a quien lo tiene.
export function useServicios() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede, permisosListos } = usePermissions()
  const incluirPrecio = puede.verHonorariosCliente === true

  const permitida = permisosListos === true
  return conRefetchSoloPermitido(
    useQuery({
      queryKey: [...serviciosKeys.list(), organizacionId, incluirPrecio] as const,
      queryFn: () => {
        if (!organizacionId) throw new Error('No organization')
        return serviciosService.listar(supabase!, organizacionId, incluirPrecio)
      },
      enabled: !!supabase && !!organizacionId && permitida,
    }),
    permitida,
  )
}

// Hook para obtener un servicio por ID
export function useServicio(id: string) {
  const { supabase } = useSupabase()
  const { puede, permisosListos } = usePermissions()
  const incluirPrecio = puede.verHonorariosCliente === true

  const permitida = permisosListos === true
  return conRefetchSoloPermitido(
    useQuery({
      queryKey: [...serviciosKeys.detail(id), incluirPrecio] as const,
      queryFn: () => serviciosService.obtenerPorId(supabase!, id, incluirPrecio),
      enabled: !!id && !!supabase && permitida,
    }),
    permitida,
  )
}

// Hook para obtener clientes de un servicio
// Trae el precio de cada cliente: sin verHonorariosCliente la query no corre.
export function useClientesDelServicio(servicioId: string) {
  const { supabase } = useSupabase()
  const { puede, permisosListos } = usePermissions()

  const permitida = permisosListos === true && puede.verHonorariosCliente === true
  return conRefetchSoloPermitido(
    useQuery({
      queryKey: serviciosKeys.clientes(servicioId),
      queryFn: () => serviciosService.obtenerClientesDelServicio(supabase!, servicioId),
      enabled: !!servicioId && !!supabase && permitida,
    }),
    permitida,
  )
}

// Hook para crear servicio
export function useCrearServicio() {
  const { supabase } = useSupabase()
  const queryClient = useQueryClient()
  const { puede } = usePermissions()
  const { organizacionId } = useOrganizacion()

  return useMutation({
    mutationFn: (servicio: Omit<ServicioInsert, 'organizacion_id'>) => {
      assertPermiso(puede, 'editarCatalogoServicios')
      if (!organizacionId) throw new Error('Sin organización activa')
      return serviciosService.crear(supabase!, {
        ...servicio,
        organizacion_id: organizacionId,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviciosKeys.lists() })
    },
  })
}

// Hook para actualizar servicio
export function useActualizarServicio() {
  const { supabase } = useSupabase()
  const queryClient = useQueryClient()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ServicioUpdate }) => {
      assertPermiso(puede, 'editarCatalogoServicios')
      return serviciosService.actualizar(supabase!, id, data)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: serviciosKeys.lists() })
      queryClient.invalidateQueries({ queryKey: serviciosKeys.detail(variables.id) })
    },
  })
}

// Hook para eliminar servicio
export function useEliminarServicio() {
  const { supabase } = useSupabase()
  const queryClient = useQueryClient()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: (id: string) => {
      assertPermiso(puede, 'eliminarServicio')
      return serviciosService.eliminar(supabase!, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviciosKeys.lists() })
    },
  })
}

// Hook para cambiar estado de servicio (activo/inactivo)
export function useToggleServicioEstado() {
  const { supabase } = useSupabase()
  const queryClient = useQueryClient()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: ({ id, nuevoEstado }: { id: string; nuevoEstado: 'activo' | 'inactivo' }) => {
      assertPermiso(puede, 'editarCatalogoServicios')
      return serviciosService.toggleEstado(supabase!, id, nuevoEstado)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: serviciosKeys.lists() })
      queryClient.invalidateQueries({ queryKey: serviciosKeys.detail(variables.id) })
    },
  })
}

// Hook para obtener servicios de un cliente con precios
// Trae los servicios con precio del cliente: sin verHonorariosCliente la query no corre.
export function useServiciosDelCliente(clienteId: string) {
  const { supabase } = useSupabase()
  const { puede, permisosListos } = usePermissions()

  const permitida = permisosListos === true && puede.verHonorariosCliente === true
  return conRefetchSoloPermitido(
    useQuery({
      queryKey: serviciosKeys.cliente(clienteId),
      queryFn: () => serviciosService.obtenerServiciosCliente(supabase!, clienteId),
      enabled: !!clienteId && !!supabase && permitida,
    }),
    permitida,
  )
}
